import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { MapNode, NodeType, NodeStatus } from '../types/Map.types';
import { SfxManager } from '../utils/SfxManager';
import { MusicManager } from '../systems/MusicManager';
import { isTouchDevice } from '../utils/DeviceUtils';
import { ensureTouchTarget } from '../ui/MobileTouchUI';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { TutorialOverlay } from '../ui/TutorialOverlay';
import { FONT_BODY } from '../utils/Typography';

const MAP_TUTO_KEY = 'deadtide_map_tuto_done';

// === SCÈNE DE CARTE ===
// Affiche l'arbre de chemins style Slay the Spire.
// Le joueur clique sur un nœud disponible pour y aller.

const NODE_COLORS: Record<NodeType, number> = {
  combat: 0xe74c3c,
  elite: 0xf39c12,
  boss: 0x9b59b6,
  rest: 0x2ecc71,
  event: 0x3498db,
  shop: 0xe67e22,
  explore: 0x1abc9c,
  survivor: 0xf39c12,
  extract: 0x27ae60,
};

const NODE_EMOJIS: Record<NodeType, string> = {
  combat: '⚔️',
  elite: '💀',
  boss: '👹',
  rest: '🏕️',
  event: '❓',
  shop: '🏪',
  explore: '🔍',
  survivor: '👤',
  extract: '⛵',
};

const NODE_LABELS: Record<NodeType, string> = {
  combat: 'Combat',
  elite: 'Élite',
  boss: 'BOSS',
  rest: 'Repos',
  event: 'Événement',
  shop: 'Marchand',
  explore: 'Exploration',
  survivor: 'Survivant',
  extract: 'Extraction',
};


// Dimensions de la carte (adaptées mobile/desktop)
const MAP_PADDING_X = GAME_W < 500 ? 30 : 80;
const MAP_PADDING_TOP = 90;
const MAP_PADDING_BOTTOM = GAME_H < 750 ? 60 : 80;
const MAP_WIDTH = GAME_W - MAP_PADDING_X * 2;
const MAP_HEIGHT = GAME_H - MAP_PADDING_TOP - MAP_PADDING_BOTTOM;
const NODE_RADIUS = GAME_W < 500 ? 14 : 18;

export class MapScene extends Phaser.Scene {
  private run!: RunManager;
  private sfx = new SfxManager();
  private nodeSprites: Map<string, Phaser.GameObjects.Arc> = new Map();
  private tooltipText!: Phaser.GameObjects.Text;
  private pendingNodeId: string | null = null; // Nœud sélectionné en attente de confirmation
  private confirmText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: 'MapScene' });
  }

  init(data: { run?: RunManager }): void {
    this.run = data?.run ?? new RunManager();
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.nodeSprites.clear();

    // Fond coloré selon l'acte
    const map = this.run.getMap();
    const currentFloor = this.getCurrentFloor();
    const act = currentFloor <= 4 ? 1 : currentFloor <= 8 ? 2 : 3;
    const bgColor = act === 1 ? 0x0a0a1e : act === 2 ? 0x1a0a0a : 0x0f0a1e;
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, bgColor);
    // Musique de la carte
    MusicManager.crossfadeTo(this, 'map');

    // Titre avec nom d'acte
    const actNames = { 1: 'L\'Archipel', 2: 'Les Récifs Sanglants', 3: 'Le Temple Englouti' } as Record<number, string>;
    const actColors = { 1: '#3498db', 2: '#e74c3c', 3: '#8e44ad' } as Record<number, string>;
    this.add.text(GAME_CX, 20, `🗺️ ${actNames[act]}`, {
      fontSize: '20px', color: actColors[act], fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    const hp = this.run.getPlayerHp();
    const hpStr = hp > 0 ? `❤️ ${hp} HP` : '';
    const inv = this.run.getInventory();
    this.add.text(GAME_CX, 48, `Acte ${act} — Étage ${Math.min(currentFloor + 1, map.totalFloors - 1)}    💰 ${this.run.getGold()} Or    ${hpStr}    🎒 ${inv.getTransportCount()}/${inv.getTransportCapacity()}`, {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
      wordWrap: { width: GAME_W - 20 }, align: 'center',
    }).setOrigin(0.5);

    // Bouton inventaire — aligné droite avec offset pour éviter les boutons
    // globaux 💡 (à GAME_W-38) et 🐛 (à GAME_W-6) en haut-droite.
    const equipBtn = this.add.text(GAME_W - 65, 15, '⚓ Équip', {
      fontSize: '11px', color: '#3498db', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 6, y: 3 },
    }).setOrigin(1, 0);
    equipBtn.setInteractive({ useHandCursor: true });
    equipBtn.on('pointerdown', () => {
      startSceneWithFade(this, 'EquipScene', { run: this.run });
    });
    equipBtn.on('pointerover', () => equipBtn.setColor('#2980b9'));
    equipBtn.on('pointerout', () => equipBtn.setColor('#3498db'));

    // Bouton "Revoir le tuto" en haut-gauche — utile pendant l'expédition
    // pour relire les explications de la carte sans devoir reset les flags.
    const helpBtn = this.add.text(10, 15, '❓ Tuto', {
      fontSize: '11px', color: '#9b59b6', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 6, y: 3 },
    });
    helpBtn.setInteractive({ useHandCursor: true });
    helpBtn.on('pointerdown', () => this.showMapTutorial(true));
    helpBtn.on('pointerover', () => helpBtn.setColor('#c39bd3'));
    helpBtn.on('pointerout', () => helpBtn.setColor('#9b59b6'));

    // Légende
    this.drawLegend();

    // Dessiner les connexions (lignes) d'abord
    this.drawConnections();

    // Dessiner les nœuds par-dessus
    this.drawNodes();

    // Tooltip (caché par défaut)
    this.tooltipText = this.add.text(0, 0, '', {
      fontSize: '11px', color: '#ffffff', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 6, y: 4 },
    }).setDepth(100).setVisible(false);

    // Tutoriel première visite de la carte
    this.showMapTutorial();
  }

  /**
   * Tutoriel de la carte. À la 1ère visite il s'affiche automatiquement (avec
   * `force=false`). Le bouton "❓ Tuto" en haut-gauche le relance à la demande
   * via `force=true` (skip du check de flag).
   */
  private showMapTutorial(force = false): void {
    if (!force) {
      let done = false;
      try { done = localStorage.getItem(MAP_TUTO_KEY) === '1'; } catch { /* */ }
      if (done) return;
    }

    const tuto = new TutorialOverlay(this, [
      {
        text: '🗺️ La Carte de l\'Expédition\n\nChaque nœud mène à un lieu différent.\nCliquez sur un nœud lumineux (pulsant)\npour vous y rendre.',
        x: GAME_CX, y: GAME_CY - 60,
      },
      {
        text: '⚔️ Combat — Ennemis normaux\n💀 Élite — Ennemis coriaces, meilleur loot\n👹 BOSS — Gardien de fin d\'acte\n🏕️ Repos — Récupérez des HP (+30%)',
        x: GAME_CX, y: GAME_CY - 60,
        arrowTo: { x: GAME_CX, y: GAME_H - 20 },
      },
      {
        text: '❓ Événement — Choix narratifs (ressources, or…)\n🏪 Marchand — Achetez équipements et soins\n🔍 Exploration — Récoltez des ressources\n👤 Survivant — Recrutez un allié',
        x: GAME_CX, y: GAME_CY - 60,
        arrowTo: { x: GAME_CX, y: GAME_H - 20 },
      },
      {
        text: '⛵ Extraction — Quittez le run et\nRAMENEZ tout votre butin au camp.\nSans extraction : si vous mourez,\nvous perdez tout (permadeath) !',
        x: GAME_CX, y: GAME_CY - 40,
      },
    ], () => {
      try { localStorage.setItem(MAP_TUTO_KEY, '1'); } catch { /* */ }
    });
    tuto.start();
  }

  /** Étage actuel du joueur */
  private getCurrentFloor(): number {
    const map = this.run.getMap();
    if (!map.currentNodeId) return 0;
    const current = map.nodes.find(n => n.id === map.currentNodeId);
    return current ? current.floor : 0;
  }

  /** Convertit les coordonnées d'un nœud en position écran */
  private nodeToScreen(node: MapNode): { x: number; y: number } {
    const map = this.run.getMap();
    // Boss en haut, départ en bas
    const yRatio = 1 - node.floor / (map.totalFloors - 1);
    return {
      x: MAP_PADDING_X + node.x * MAP_WIDTH,
      y: MAP_PADDING_TOP + yRatio * MAP_HEIGHT,
    };
  }

  /** Dessine la légende en bas */
  private drawLegend(): void {
    // Ordre : combats → repos/shop → divers
    const types: NodeType[] = ['combat', 'elite', 'boss', 'rest', 'shop', 'explore', 'event', 'survivor', 'extract'];
    const y = GAME_H - 20;
    const spacing = GAME_W < 500 ? 46 : 82;
    const startX = GAME_CX - ((types.length - 1) * spacing) / 2;

    for (let i = 0; i < types.length; i++) {
      const type = types[i];
      const x = startX + i * spacing;
      this.add.circle(x - 10, y, 6, NODE_COLORS[type]);
      this.add.text(x + 2, y, NODE_EMOJIS[type], {
        fontSize: '10px', fontFamily: FONT_BODY,
      }).setOrigin(0, 0.5);
    }
  }

  /** Dessine les lignes de connexion */
  private drawConnections(): void {
    const map = this.run.getMap();
    const graphics = this.add.graphics();

    for (const node of map.nodes) {
      const from = this.nodeToScreen(node);

      for (const connId of node.connections) {
        const target = map.nodes.find(n => n.id === connId);
        if (!target) continue;
        const to = this.nodeToScreen(target);

        // Couleur selon l'état : chemin parcouru = doré, disponible = blanc, sinon gris
        const isPathTaken = node.status === 'completed' && (target.status === 'completed' || target.status === 'current');
        const isAvailable = node.status === 'completed' && target.status === 'available';

        if (isPathTaken) {
          graphics.lineStyle(3, 0xf1c40f, 0.8);
        } else if (isAvailable) {
          graphics.lineStyle(2, 0xffffff, 0.5);
        } else {
          graphics.lineStyle(1, 0x4a4a6a, 0.3);
        }

        graphics.beginPath();
        graphics.moveTo(from.x, from.y);
        graphics.lineTo(to.x, to.y);
        graphics.strokePath();
      }
    }
  }

  /** Dessine tous les nœuds */
  private drawNodes(): void {
    const map = this.run.getMap();

    for (const node of map.nodes) {
      const pos = this.nodeToScreen(node);
      const isCompleted = node.status === 'completed';
      const isCurrent = node.status === 'current';
      // 'current' = nœud sélectionné mais pas terminé (ex : combat quitté en cours)
      // → cliquable pour reprendre l'action où on l'avait laissée.
      const isClickable = node.status === 'available' || isCurrent;

      // Cercle du nœud
      const radius = node.type === 'boss' ? NODE_RADIUS + 6 : NODE_RADIUS;
      let color = NODE_COLORS[node.type];
      let alpha = 1;

      if (isCompleted) {
        color = 0x4a4a6a; // grisé
        alpha = 0.5;
      } else if (node.status === 'locked') {
        alpha = 0.3;
      }

      // Halo pour nœuds disponibles (pulsation)
      if (isClickable) {
        const halo = this.add.circle(pos.x, pos.y, radius + 6, color, 0.2);
        this.tweens.add({
          targets: halo,
          alpha: { from: 0.1, to: 0.4 },
          scaleX: { from: 1, to: 1.2 },
          scaleY: { from: 1, to: 1.2 },
          duration: 800,
          yoyo: true,
          repeat: -1,
        });
      }

      // Nœud principal
      const circle = this.add.circle(pos.x, pos.y, radius, color, alpha);
      circle.setStrokeStyle(2, isClickable ? 0xffffff : (isCurrent ? 0xf1c40f : 0x333333));
      this.nodeSprites.set(node.id, circle);

      // Icône du type (emoji texte) — depth 4 pour rester au-dessus du hitZone
      const nodeAlpha = isCompleted ? 0.4 : (node.status === 'locked' ? 0.3 : 1);
      const isBossNode = node.type === 'boss';
      this.add.text(pos.x, pos.y, NODE_EMOJIS[node.type], {
        fontSize: isBossNode ? '20px' : '16px',
        fontFamily: FONT_BODY,
      }).setOrigin(0.5).setAlpha(nodeAlpha).setDepth(4);

      // Checkmark pour les nœuds complétés
      if (isCompleted) {
        this.add.text(pos.x + radius - 2, pos.y - radius + 2, '✓', {
          fontSize: '12px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
        }).setOrigin(0.5);
      }

      // Marqueur joueur
      if (isCurrent) {
        this.add.text(pos.x, pos.y - radius - 12, '🏴‍☠️', {
          fontSize: '16px', fontFamily: FONT_BODY,
        }).setOrigin(0.5);
      }

      // Tooltip au survol (tous les nœuds) + clic sur les disponibles
      // Zone d'interaction invisible plus grande pour faciliter le survol
      const hitZone = this.add.circle(pos.x, pos.y, radius + 8, 0x000000, 0).setDepth(3);
      hitZone.setInteractive({ useHandCursor: isClickable });

      const originalStroke = isClickable ? 0xffffff : (isCurrent ? 0xf1c40f : 0x333333);
      if (!isTouchDevice()) {
        // Desktop : hover classique
        hitZone.on('pointerover', () => {
          circle.setStrokeStyle(3, 0xf1c40f);
          this.tooltipText.setText(`${NODE_EMOJIS[node.type]} ${NODE_LABELS[node.type]}`);
          this.tooltipText.setPosition(pos.x + radius + 10, pos.y - 10);
          this.tooltipText.setVisible(true);
          this.tooltipText.setDepth(100);
        });
        hitZone.on('pointerout', () => {
          circle.setStrokeStyle(2, originalStroke);
          this.tooltipText.setVisible(false);
        });
      }

      if (isClickable) {
        hitZone.on('pointerdown', () => this.selectNode(node));
      }
    }
  }

  /** Le joueur sélectionne un nœud (double-tap : preview puis confirmer) */
  private selectNode(node: MapNode): void {
    // Premier tap : afficher preview + bouton confirmer
    if (this.pendingNodeId !== node.id) {
      this.sfx.select();
      this.pendingNodeId = node.id;

      // Supprimer l'ancien bouton confirmer
      if (this.confirmText) { this.confirmText.destroy(); this.confirmText = null; }

      // Highlight le nœud sélectionné
      const circle = this.nodeSprites.get(node.id);
      if (circle) circle.setStrokeStyle(3, 0x2ecc71);

      // Afficher tooltip persistant + bouton confirmer
      const pos = this.nodeToScreen(node);
      const radius = node.type === 'boss' ? NODE_RADIUS + 6 : NODE_RADIUS;
      this.tooltipText.setText(`${NODE_EMOJIS[node.type]} ${NODE_LABELS[node.type]}`);
      this.tooltipText.setPosition(pos.x + radius + 10, pos.y - 10);
      this.tooltipText.setVisible(true);
      this.tooltipText.setDepth(100);

      this.confirmText = this.add.text(pos.x, pos.y + radius + 20, '[ ⚔️ Aller ]', {
        fontSize: '14px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
        backgroundColor: '#1a2a1a', padding: { x: 14, y: 8 },
      }).setOrigin(0.5).setDepth(100);
      this.confirmText.setInteractive({ useHandCursor: true });
      this.confirmText.on('pointerdown', () => this.confirmSelectNode(node));
      ensureTouchTarget(this.confirmText);
      return;
    }

    // Deuxième tap sur le même nœud : confirmer
    this.confirmSelectNode(node);
  }

  /** Confirme la sélection et navigue vers le nœud */
  private confirmSelectNode(node: MapNode): void {
    if (this.confirmText) { this.confirmText.destroy(); this.confirmText = null; }
    this.pendingNodeId = null;

    // Reprise d'un nœud déjà 'current' (ex : combat quitté en cours) :
    // on saute la mise à jour d'état (déjà faite) et le tracking stats
    // (déjà incrémenté à la première sélection). On route directement.
    const isResume = node.status === 'current';
    if (!isResume) {
      this.run.selectMapNode(node.id);
      this.run.getMeta().addStat('nodesVisited');
      this.run.getMeta().updateRecord('floorsReached', node.floor);
    }

    // Transition selon le type de nœud
    switch (node.type) {
      case 'combat':
      case 'elite':
        startSceneWithFade(this, 'CombatScene', { run: this.run }, 'swipe');
        break;
      case 'boss':
        startSceneWithFade(this, 'CombatScene', { run: this.run }, 'radial');
        break;
      case 'rest':
        this.handleRest();
        break;
      case 'event':
        startSceneWithFade(this, 'EventScene', { run: this.run });
        break;
      case 'shop':
        startSceneWithFade(this, 'ShopScene', { run: this.run });
        break;
      case 'explore':
        startSceneWithFade(this, 'ExploreScene', { run: this.run });
        break;
      case 'survivor':
        startSceneWithFade(this, 'SurvivorScene', { run: this.run });
        break;
      case 'extract':
        startSceneWithFade(this, 'ExtractScene', { run: this.run });
        break;
    }
  }

  /** Gère le repos : soigne 30% des HP max de chaque membre vivant */
  private handleRest(): void {
    const aliveTeam = this.run.getAliveTeam();
    if (aliveTeam.length === 0) return;

    // Soigner 30% des HP max de chaque membre vivant de l'équipe
    const results = this.run.healAliveTeam(0.3);

    // Marquer le nœud comme complété et revenir à la carte
    this.run.completeCurrentNode();

    // Feedback visuel : une ligne par membre soigné
    const lines = results.map(r => `${r.name}: +${r.healed} HP`).join('\n');
    const text = this.add.text(GAME_CX, GAME_CY, `🏕️ Repos\n${lines}`, {
      fontSize: '20px', color: '#2ecc71', fontFamily: FONT_BODY, align: 'center',
    }).setOrigin(0.5).setAlpha(0);

    this.tweens.add({
      targets: text,
      alpha: 1,
      duration: 500,
      onComplete: () => {
        this.time.delayedCall(1200, () => {
          startSceneWithFade(this, 'MapScene', { run: this.run });
        });
      },
    });
  }
}
